import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, switchMap, from, tap, firstValueFrom } from 'rxjs';
import { Storage } from '@ionic/storage-angular';
import { APP_SETTINGS, AppSettings } from './runtime-settings';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private readonly appConfig: AppSettings = inject(APP_SETTINGS);
    private readonly storage = inject(Storage);
    private readonly location = inject(Location);

    private loggedIn = signal(false);
    private initialized = signal(false);

    isLoggedIn = computed(() => this.loggedIn());
    isInitialized = computed(() => this.initialized());

    constructor(private http: HttpClient, private router: Router) {}

    private get _authURL(): string { return `${this.appConfig.apiBaseUrl}/rpc/auth/login`; }
    private get _checkTokenValidityURL(): string { return `${this.appConfig.apiBaseUrl}/rpc/check_token_validity`; }
    private get _logoutURL(): string { return `${this.appConfig.apiBaseUrl}/rpc/auth/logout`; }
    private get _refreshTokenURL(): string { return `${this.appConfig.apiBaseUrl}/rpc/auth/refresh_token`; }

    // #region LOGIN FUNCTIONS
    // login with email and password
    // returns token, email and start route
    login(email: string, password: string): Observable<any> {
        const postData = { 
            p_email: email, 
            p_password: password 
        };

        return this.http.post(this._authURL, postData)
            .pipe(
                switchMap((data:any) => 
                    from(Promise.all([
                        this.storage.set('planski_token', data.token),
                        this.storage.set('planski_email', email),
                        this.storage.set('planski_start_route', '/app/tabs/tasks'),
                        this.loggedIn.set(true)
                    ])).pipe(
                        map(() => data)
                    )
                )
            )
    }

    // logout user
    async logout(): Promise<void> {
        if (this.loggedIn()) {
            this.refreshPromise = null; // don't leave in-flight refresh for next session
            const fcmToken = await this.storage.get('planski_fcm_token');
            await firstValueFrom(this.http.post(this._logoutURL, { p_notification_token: fcmToken }));
            await this.storage.remove('planski_fcm_token');
            this.loggedIn.set(false);
        }
        await this.storage.remove('planski_token');
        await this.storage.remove('planski_email');
    }

    async getUserEmail(): Promise<string | null> {
        return await this.storage.get('planski_email');
    }

    async getToken(): Promise<string | null> {
        return await this.storage.get('planski_token');
    }

    removeToken(): void {
        this.storage.remove('planski_token');
    }

    async getStartRoute(): Promise<string | null> {
        return await this.storage.get('planski_start_route');
    }
    
    async checkLoginStatus(): Promise<void> {
        try {
            const token = await this.storage.get('planski_token');
            if (token) {
                await this.checkTokenValidity();
                if (!this.loggedIn()) {
                    await this.logout();
                }
            } else {
                this.loggedIn.set(false);
            }
        } catch {
            //this.loggedIn.set(false);
        }
        finally {
            this.initialized.set(true);
        }
    }
    
    // check if user has a valid token
    // returns true if user has a valid token, false otherwise
    async checkTokenValidity(): Promise<void> {
        const data = await firstValueFrom(this.http.get<{ loggedin: boolean }>(this._checkTokenValidityURL));
        this.loggedIn.set(data?.loggedin);
    }

    /** In-flight refresh promise so concurrent 401s wait for the same refresh (single-flight). */
    private refreshPromise: Promise<boolean> | null = null;

    /**
     * Refresh token with single-flight: if a refresh is already in progress,
     * wait for it and return the same result instead of triggering another.
     */
    async refreshTokenOnce(loginOnSuccess: boolean = false): Promise<boolean> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }
        this.refreshPromise = this.refreshToken(loginOnSuccess).finally(() => {
            this.refreshPromise = null;
        });
        return this.refreshPromise;
    }

    async refreshToken(loginOnSuccess: boolean = false): Promise<boolean> {
        try {
            const res = await firstValueFrom(this.http.post<any>(this._refreshTokenURL, {}, { withCredentials: true }));
            if (res?.token) {
                this.loggedIn.set(true);
                await this.storage.set('planski_token', res.token);
                //if (loginOnSuccess) {
                    //await this.navigateToStartRoute();
                //}
            } else {
                await this.logout();
            }
            return true;
        } catch {
            return false;
        }
    }

    async navigateToStartRoute(): Promise<void> {
        //this.location.replaceState('/login'); // rewrite current history entry
        await this.router.navigate([await this.getStartRoute()]);  // now navigate forward, login slot is gone
    }

    // check if user has token
    async hasToken(): Promise<boolean> {
        const token = await this.storage.get('planski_token');
        return token !== null;
    }
    // #endregion
}
